import {
  useState
} from "react";

import type {
  FormEvent
} from "react";

import {
  ArrowLeft,
  LoaderCircle,
  MapPin,
  MessageCircle,
  PackageCheck,
  ShoppingBag
} from "lucide-react";

import {
  Link,
  useNavigate
} from "react-router";

import {
  useCart
} from "../context/CartContext";

import {
  createOrder
} from "../services/orders";

import type {
  CreatedOrder
} from "../services/orders";

function cleanPhone(
  value: string
): string {
  return value.replace(
    /\D/g,
    ""
  );
}

function buildWhatsAppMessage(
  order: CreatedOrder
): string {
  const itemLines =
    order.items.map(
      (
        item,
        index
      ) =>
        `${index + 1}. ${item.productName} x${item.quantity} - ₹${Number(
          item.lineTotal
        ).toLocaleString("en-IN")}`
    );

  const address =
    [
      order.addressLine1,
      order.addressLine2,
      order.city,
      order.state,
      order.postalCode
    ]
      .filter(Boolean)
      .join(", ");

  return [
    "Hello RiNi Bodycare,",
    "",
    "I have placed an order through rinibodycare.com.",
    "",
    `Order Number: ${order.orderNumber}`,
    "",
    "Order Items:",
    ...itemLines,
    "",
    `Subtotal: ₹${Number(order.subtotal).toLocaleString("en-IN")}`,
    `Order Total: ₹${Number(order.totalAmount).toLocaleString("en-IN")}`,
    "",
    "Customer Details:",
    `Name: ${order.customerName}`,
    `Phone: ${order.phone}`,
    `Delivery Address: ${address}`,
    "",
    order.notes
      ? `Order Note: ${order.notes}`
      : "",
    "",
    "Payment Method: Manual payment",
    "",
    "Please confirm my order and payment details.",
    "Thank you."
  ]
    .filter(
      (line) =>
        line !== ""
        || true
    )
    .join("\n");
}

export function CheckoutPage() {
  const {
    items,
    subtotal,
    clearCart
  } =
    useCart();

  const navigate =
    useNavigate();

  const [
    isSubmitting,
    setIsSubmitting
  ] =
    useState(false);

  const [
    errorMessage,
    setErrorMessage
  ] =
    useState<string | null>(
      null
    );

  const [
    form,
    setForm
  ] =
    useState({
      customerName: "",
      phone: "",
      whatsappNumber: "",
      email: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "Karnataka",
      postalCode: "",
      notes: ""
    });

  function updateField(
    field: keyof typeof form,
    value: string
  ) {
    setForm(
      (current) => ({
        ...current,
        [field]: value
      })
    );
  }

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>
  ) {
    event.preventDefault();

    setErrorMessage(
      null
    );

    if (
      items.length === 0
    ) {
      setErrorMessage(
        "Your cart is empty."
      );

      return;
    }

    const phone =
      cleanPhone(
        form.phone
      );

    const whatsapp =
      cleanPhone(
        form.whatsappNumber
      );

    if (
      phone.length < 10
    ) {
      setErrorMessage(
        "Please enter a valid mobile number."
      );

      return;
    }

    try {
      setIsSubmitting(
        true
      );

      const order =
        await createOrder({
          customerName:
            form.customerName.trim(),

          phone,

          whatsappNumber:
            whatsapp ||
            phone,

          email:
            form.email.trim() ||
            null,

          addressLine1:
            form.addressLine1.trim(),

          addressLine2:
            form.addressLine2.trim() ||
            null,

          city:
            form.city.trim(),

          state:
            form.state.trim(),

          postalCode:
            form.postalCode.trim(),

          notes:
            form.notes.trim() ||
            null,

          items:
            items.map(
              (item) => ({
                productId:
                  item.product.id,

                quantity:
                  item.quantity
              })
            )
        });

      const whatsappNumber =
        String(
          import.meta.env
            .VITE_WHATSAPP_ORDER_NUMBER ??
            ""
        ).replace(
          /\D/g,
          ""
        );

      const message =
        buildWhatsAppMessage(
          order
        );

      const whatsappUrl =
        whatsappNumber
          ? `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
              message
            )}`
          : "";

      sessionStorage.setItem(
        "rini_last_order",
        JSON.stringify({
          order,
          whatsappUrl
        })
      );

      clearCart();

      navigate(
        `/order-success?order=${encodeURIComponent(
          order.orderNumber
        )}`,
        {
          state: {
            order,
            whatsappUrl
          }
        }
      );
    } catch (error) {
      console.error(
        "Order creation failed",
        error
      );

      setErrorMessage(
        "Your order could not be submitted. Please verify your details and try again."
      );
    } finally {
      setIsSubmitting(
        false
      );
    }
  }

  if (
    items.length === 0
  ) {
    return (
      <main className="rini-section">
        <div className="rini-container text-center">
          <ShoppingBag
            size={42}
            className="mx-auto text-[#70845b]"
          />

          <h1 className="rini-heading mt-6 text-5xl">
            Your cart is empty
          </h1>

          <p className="mx-auto mt-4 max-w-xl leading-7 text-[#746d65]">
            Add some RiNi favourites before continuing to checkout.
          </p>

          <Link
            to="/shop"
            className="rini-button-primary mt-8"
          >
            Return to shop
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-[#fffdf8] py-8 sm:py-12 lg:py-20">
      <div className="rini-container">
        <Link
          to="/cart"
          className="inline-flex items-center gap-2 text-sm font-medium text-[#5e3b2c]"
        >
          <ArrowLeft
            size={17}
          />

          Back to cart
        </Link>

        <div className="mt-8 grid gap-12 lg:grid-cols-[1fr_390px]">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
              Complete your order
            </p>

            <h1 className="rini-heading mt-3 text-5xl">
              Checkout
            </h1>

            <p className="mt-4 max-w-2xl leading-7 text-[#746d65]">
              Enter your delivery details. No online payment is required.
              The RiNi team will contact you to confirm payment and delivery.
            </p>

            <form
              onSubmit={
                handleSubmit
              }
              className="mt-10 space-y-8"
            >
              <section className="rounded-[2rem] border border-[#5e3b2c]/10 bg-white p-6 sm:p-8">
                <div className="flex items-center gap-3">
                  <MessageCircle
                    size={21}
                    className="text-[#70845b]"
                  />

                  <h2 className="font-serif text-2xl text-[#38251e]">
                    Contact information
                  </h2>
                </div>

                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  <label className="sm:col-span-2">
                    <span className="mb-2 block text-sm font-semibold text-[#38251e]">
                      Full name *
                    </span>

                    <input
                      required
                      value={
                        form.customerName
                      }
                      onChange={(event) =>
                        updateField(
                          "customerName",
                          event.target.value
                        )
                      }
                      className="w-full rounded-2xl border border-[#5e3b2c]/15 bg-[#fffdf8] px-4 py-3.5 outline-none transition focus:border-[#70845b]"
                      placeholder="Your full name"
                    />
                  </label>

                  <label>
                    <span className="mb-2 block text-sm font-semibold text-[#38251e]">
                      Mobile number *
                    </span>

                    <input
                      required
                      inputMode="tel"
                      value={
                        form.phone
                      }
                      onChange={(event) =>
                        updateField(
                          "phone",
                          event.target.value
                        )
                      }
                      className="w-full rounded-2xl border border-[#5e3b2c]/15 bg-[#fffdf8] px-4 py-3.5 outline-none transition focus:border-[#70845b]"
                      placeholder="9876543210"
                    />
                  </label>

                  <label>
                    <span className="mb-2 block text-sm font-semibold text-[#38251e]">
                      WhatsApp number
                    </span>

                    <input
                      inputMode="tel"
                      value={
                        form.whatsappNumber
                      }
                      onChange={(event) =>
                        updateField(
                          "whatsappNumber",
                          event.target.value
                        )
                      }
                      className="w-full rounded-2xl border border-[#5e3b2c]/15 bg-[#fffdf8] px-4 py-3.5 outline-none transition focus:border-[#70845b]"
                      placeholder="Same as mobile if blank"
                    />
                  </label>

                  <label className="sm:col-span-2">
                    <span className="mb-2 block text-sm font-semibold text-[#38251e]">
                      Email
                    </span>

                    <input
                      type="email"
                      value={
                        form.email
                      }
                      onChange={(event) =>
                        updateField(
                          "email",
                          event.target.value
                        )
                      }
                      className="w-full rounded-2xl border border-[#5e3b2c]/15 bg-[#fffdf8] px-4 py-3.5 outline-none transition focus:border-[#70845b]"
                      placeholder="Optional"
                    />
                  </label>
                </div>
              </section>

              <section className="rounded-[2rem] border border-[#5e3b2c]/10 bg-white p-6 sm:p-8">
                <div className="flex items-center gap-3">
                  <MapPin
                    size={21}
                    className="text-[#70845b]"
                  />

                  <h2 className="font-serif text-2xl text-[#38251e]">
                    Delivery address
                  </h2>
                </div>

                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  <label className="sm:col-span-2">
                    <span className="mb-2 block text-sm font-semibold text-[#38251e]">
                      Address *
                    </span>

                    <input
                      required
                      value={
                        form.addressLine1
                      }
                      onChange={(event) =>
                        updateField(
                          "addressLine1",
                          event.target.value
                        )
                      }
                      className="w-full rounded-2xl border border-[#5e3b2c]/15 bg-[#fffdf8] px-4 py-3.5 outline-none focus:border-[#70845b]"
                      placeholder="House / Flat / Street / Area"
                    />
                  </label>

                  <label className="sm:col-span-2">
                    <span className="mb-2 block text-sm font-semibold text-[#38251e]">
                      Landmark / additional address
                    </span>

                    <input
                      value={
                        form.addressLine2
                      }
                      onChange={(event) =>
                        updateField(
                          "addressLine2",
                          event.target.value
                        )
                      }
                      className="w-full rounded-2xl border border-[#5e3b2c]/15 bg-[#fffdf8] px-4 py-3.5 outline-none focus:border-[#70845b]"
                      placeholder="Optional"
                    />
                  </label>

                  <label>
                    <span className="mb-2 block text-sm font-semibold text-[#38251e]">
                      City *
                    </span>

                    <input
                      required
                      value={
                        form.city
                      }
                      onChange={(event) =>
                        updateField(
                          "city",
                          event.target.value
                        )
                      }
                      className="w-full rounded-2xl border border-[#5e3b2c]/15 bg-[#fffdf8] px-4 py-3.5 outline-none focus:border-[#70845b]"
                    />
                  </label>

                  <label>
                    <span className="mb-2 block text-sm font-semibold text-[#38251e]">
                      State *
                    </span>

                    <input
                      required
                      value={
                        form.state
                      }
                      onChange={(event) =>
                        updateField(
                          "state",
                          event.target.value
                        )
                      }
                      className="w-full rounded-2xl border border-[#5e3b2c]/15 bg-[#fffdf8] px-4 py-3.5 outline-none focus:border-[#70845b]"
                    />
                  </label>

                  <label>
                    <span className="mb-2 block text-sm font-semibold text-[#38251e]">
                      PIN code *
                    </span>

                    <input
                      required
                      inputMode="numeric"
                      value={
                        form.postalCode
                      }
                      onChange={(event) =>
                        updateField(
                          "postalCode",
                          event.target.value
                        )
                      }
                      className="w-full rounded-2xl border border-[#5e3b2c]/15 bg-[#fffdf8] px-4 py-3.5 outline-none focus:border-[#70845b]"
                    />
                  </label>
                </div>
              </section>

              <section className="rounded-[2rem] border border-[#5e3b2c]/10 bg-white p-6 sm:p-8">
                <h2 className="font-serif text-2xl text-[#38251e]">
                  Order notes
                </h2>

                <textarea
                  value={
                    form.notes
                  }
                  onChange={(event) =>
                    updateField(
                      "notes",
                      event.target.value
                    )
                  }
                  rows={4}
                  className="mt-5 w-full rounded-2xl border border-[#5e3b2c]/15 bg-[#fffdf8] px-4 py-3.5 outline-none focus:border-[#70845b]"
                  placeholder="Gift message, delivery preference or other instructions..."
                />
              </section>

              {errorMessage && (
                <div className="rounded-2xl bg-red-50 px-5 py-4 text-sm text-red-700">
                  {errorMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={
                  isSubmitting
                }
                className="rini-button-primary min-w-52 justify-center disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? (
                  <>
                    <LoaderCircle
                      size={18}
                      className="animate-spin"
                    />

                    Placing order...
                  </>
                ) : (
                  <>
                    <PackageCheck
                      size={18}
                    />

                    Place order
                  </>
                )}
              </button>
            </form>
          </div>

          <aside className="h-fit rounded-[2rem] bg-[#efe3d2] p-7 lg:sticky lg:top-28">
            <h2 className="font-serif text-2xl text-[#38251e]">
              Order summary
            </h2>

            <div className="mt-6 space-y-4">
              {items.map(
                (item) => (
                  <div
                    key={
                      item.product.id
                    }
                    className="flex justify-between gap-4 border-b border-[#5e3b2c]/10 pb-4"
                  >
                    <div>
                      <p className="text-sm font-medium text-[#38251e]">
                        {item.product.name}
                      </p>

                      <p className="mt-1 text-xs text-[#746d65]">
                        Qty {item.quantity}
                      </p>
                    </div>

                    <p className="text-sm font-semibold text-[#38251e]">
                      ₹
                      {(
                        Number(
                          item.product.price
                        ) *
                        item.quantity
                      ).toLocaleString(
                        "en-IN"
                      )}
                    </p>
                  </div>
                )
              )}
            </div>

            <div className="mt-6 flex justify-between">
              <span className="text-[#746d65]">
                Subtotal
              </span>

              <span className="font-semibold text-[#38251e]">
                ₹
                {subtotal.toLocaleString(
                  "en-IN"
                )}
              </span>
            </div>

            <div className="mt-3 flex justify-between gap-4 text-sm">
              <span className="text-[#746d65]">
                Shipping
              </span>

              <span className="text-right font-medium text-[#38251e]">
                To be confirmed
              </span>
            </div>

            <div className="mt-5 flex justify-between border-t border-[#5e3b2c]/10 pt-5">
              <span className="font-serif text-xl text-[#38251e]">
                Product subtotal
              </span>

              <span className="text-xl font-semibold text-[#5e3b2c]">
                ₹
                {subtotal.toLocaleString(
                  "en-IN"
                )}
              </span>
            </div>

            <div className="mt-6 rounded-2xl bg-white/70 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#70845b]">
                Payment
              </p>

              <p className="mt-2 text-sm leading-6 text-[#746d65]">
                No online payment is required now.
                RiNi will review your delivery location and package size,
                confirm the shipping charge, and share the final payable
                amount through WhatsApp.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
